import {PDFDocumentProxy} from 'pdfjs-dist';
import {useCallback, useEffect, useRef, useState} from 'react';

import {getPdfHighlights, PdfHighlight} from './PdfHighlight.utils';

type UsePdfHighlights = {
  highlights: PdfHighlight[];
  /**
   * Reads the highlights of the given document, or resolves with `null` if the
   * viewer moved on to another document while reading.
   */
  loadHighlights: (
    document: PDFDocumentProxy
  ) => Promise<PdfHighlight[] | null>;
  resetHighlights: () => void;
};

export function usePdfHighlights(): UsePdfHighlights {
  const [highlights, setHighlights] = useState<PdfHighlight[]>([]);
  // Scanning a document for highlights outlives the document itself when the
  // viewer switches to another file, so the scan has to be told that its
  // results are stale.
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const resetHighlights = useCallback(() => {
    abortRef.current?.abort();
    setHighlights([]);
  }, []);

  const loadHighlights = useCallback(async (document: PDFDocumentProxy) => {
    abortRef.current?.abort();
    const abortController = new AbortController();
    abortRef.current = abortController;

    const pdfHighlights = await getPdfHighlights(
      document,
      abortController.signal
    );
    if (!pdfHighlights) {
      return null;
    }

    setHighlights(pdfHighlights);
    return pdfHighlights;
  }, []);

  return {highlights, loadHighlights, resetHighlights};
}
